import { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import styles from './Cases.module.css';
import openpic from '../../assets/open.png';
import closedpic from '../../assets/closed.png';
import progresspic from '../../assets/progress.png';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../assets/styles/main.css';
import { casesUrl } from '../../constants/apiEndpoints';

const Cases = () => {
    const [filteredData, setFilteredData] = useState([]);
    const [allData, setAllData] = useState([]);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [page, setPage] = useState(0);
    const [count, setCount] = useState();
    const [searchTerm, setSearchTerm] = useState('');

    const location = useLocation();
    const navigate = useNavigate();

    const getPicture = (category) => {
        if (category === 'Open') {
            return (
                <div className='flex align-items-center'>
                    <img src={openpic} height="15" width="15" className='mr-2' alt="Open Status" />
                    Open
                </div>
            );
        } else if (category === 'Closed') {
            return (
                <div className='flex align-items-center'>
                    <img src={closedpic} height="15" width="15" className='mr-2' alt="Closed Status" />
                    Closed
                </div>
            );
        } else if (category === 'In Progress') {
            return (
                <div className='flex align-items-center'>
                    <img src={progresspic} height="15" width="15" className='mr-2' alt="In Progress Status" />
                    In Progress
                </div>
            );
        } else {
            return (
                <div className='flex align-items-center'>
                    <i className="pi pi-times-circle mr-2" style={{ color: 'red' }}></i>
                    {category}
                </div>
            );
        }
    };

    const formattedDate = (dates) => {
        if (!dates) return 'NA';
        const date = new Date(dates);
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const columns = [
        { field: 'serialNo', header: 'S.No', sortable: true },
        {
            field: 'acknowledge_no',
            header: 'Acknowledge number',
            sortable: true,
            body: (rowData) => {
                const isActionable = ['closed', 'failed'].includes(rowData.status?.toLowerCase());
                return (
                    <span
                        onClick={() => {
                            if (isActionable) {
                                navigate('/action', {
                                    state: {
                                        acknowledge_no: rowData.acknowledge_no,
                                        job_id: rowData.job_id
                                    }
                                });
                            }
                        }}
                        style={{
                            color: isActionable ? '#0096CC' : '#4b5563',
                            cursor: isActionable ? 'pointer' : 'default'
                        }}
                    >
                        {rowData.acknowledge_no}
                    </span>
                );
            }
        },
        { field: 'account_number', header: 'Account Number', sortable: true },
        { field: 'job_id', header: 'Job ID', sortable: true },
        { field: 'requested_with', header: 'Requested With', sortable: true },
        {
            field: 'date_time',
            header: 'Date',
            body: (rowData) => formattedDate(rowData.date_time),
            sortable: true
        },
        {
            field: 'status',
            header: 'Status',
            body: (rowData) => getPicture(rowData.status),
            sortable: true
        },
    ];

    const onPageChange = (event) => {
        setPage(event.page);
        setFirst(event.first);
        setRows(event.rows);
    };

    const { isLoading, data, isError, error } = useQuery({
        queryKey: ['cases'],
        queryFn: () => axios.get(casesUrl)
            .then(res => res.data)
            .catch(error => {
                console.error("Error occurred while fetching cases:", error);
                throw error;
            }),
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        if (data && data.message) {
            const updatedData = data.message.map((item, index) => ({
                ...item,
                serialNo: index + 1
            }));
            setAllData(updatedData);
            setFilteredData(updatedData);
        }
    }, [data]);

    useEffect(() => {
        setFirst(0);
        setPage(0);
        if (!searchTerm.trim()) {
            setFilteredData(allData);
        } else {
            const lowerSearch = searchTerm.toLowerCase();
            const filtered = allData.filter(item =>
                String(item.acknowledge_no || "").toLowerCase().includes(lowerSearch)
            );
            setFilteredData(filtered);
        }
    }, [searchTerm, allData]);

    if (isLoading) {
        return (
            <div className="centered-container flex justify-content-center align-items-center">
                <ProgressSpinner className="custom-spinner" strokeWidth="4" animationDuration=".9s" />
            </div>
        );
    }

    if (isError) {
        const res = error.response?.data;
        const message = res?.message;
        let displayMessage = "Error fetching details";
        if (message === "No record found") {
            displayMessage = "No records found";
        } else if (message === "Dont have permission to view") {
            displayMessage = "You are not authorized to view";
        }
        return (
            <div className='empty flex justify-content-center align-items-center'>
                {displayMessage}
            </div>
        );
    }

    return (
        <div className='pr-4 pl-4' >
            <div className="flex justify-content-end align-items-center mt-3 mb-3">
                <div className='searchField'>
                    <IconField iconPosition="right">
                        <InputIcon className="pi pi-search search-icon-local" />
                        <InputText
                            placeholder="Search ACK..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </IconField>
                </div>
            </div>
            <DataTable
                scrollable
                paginator
                rows={rows}
                rowsPerPageOptions={[5, 10, 15, 20]}
                scrollHeight={"70vh"}
                value={filteredData}
                size={"medium"}
                tableStyle={{ minWidth: "50rem" }}
                first={first}
                onPage={onPageChange}
                emptyMessage="No available data found"
            >
                {columns?.map((col) => (
                    <Column
                        key={col.field}
                        sortable={col.sortable}
                        field={col.field}
                        header={col.header}
                        body={col.body ? (rowData) => col.body(rowData) : undefined}
                    />
                ))}
            </DataTable>
        </div>
    );
};

export default Cases;